/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import { Icon } from '@iconify/react';
import { useFormik, FormikProvider } from 'formik';
import saveIcon from '@iconify/icons-eva/save-outline';
import cameraIcon from '@iconify/icons-ic/baseline-camera-alt';

// material
import { Stack, TextField, IconButton, Avatar, Badge, Autocomplete } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { editItem } from '../../fetch';
import { sessionActions } from '../../store';
import { useTranslation } from '../../common/LocalizationProvider';
import CountrySelect from '../CountrySelect';
import { selectAerosArray, selectSession } from '../../createSelectorUtils';

// ----------------------------------------------------------------------
// components

export default function ProfileForm() {
  const t = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const session = useSelector(selectSession);
  const aeros = useSelector(selectAerosArray);

  const formik = useFormik({
    initialValues: session,
    onSubmit: async (values) => {
      const body = { ...values };
      delete body.permission;

      const response = await editItem('users', session.id, body);
      if (response.ok) {
        enqueueSnackbar(t('profile_Changed'), {
          variant: 'success'
        });
        const result = await response.json();
        dispatch(sessionActions.update(await result));
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
      }

      setSubmitting(false);
    }
  });
  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setSubmitting,
    setFieldValue
  } = formik;

  const handleChangeImageFile = (event) => {
    const reader = new FileReader();
    reader.readAsDataURL(event.target.files[0]);

    reader.onload = (e) => {
      setFieldValue('userImage', e.target.result);
    };
  };
  return (
    <FormikProvider value={formik}>
      <Stack spacing={3}>
        <Stack direction="column" alignItems="center" justifyContent="space-between" sx={{ my: 4 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            badgeContent={
              <>
                <input
                  accept="image/*"
                  id="icon-button-file"
                  hidden
                  type="file"
                  onChange={handleChangeImageFile}
                />
                <label htmlFor="icon-button-file">
                  <IconButton
                    sx={{
                      color: 'black',
                      backgroundColor: '#D3D3D3'
                    }}
                    aria-label="upload picture"
                    component="span"
                  >
                    <Icon icon={cameraIcon} width={30} height={30} />
                  </IconButton>
                </label>
              </>
            }
          >
            <Avatar
              alt="UserImage"
              sx={{ width: 200, height: 200 }}
              src={getFieldProps('userImage').value}
            />
          </Badge>
        </Stack>

        <TextField
          fullWidth
          autoComplete="email"
          type="email"
          label={t('fields_Email')}
          {...getFieldProps('email')}
          value={getFieldProps('email').value || ''}
          error={Boolean(touched.email && errors.email)}
          helperText={touched.email && errors.email}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label={t('fields_FirstName')}
            {...getFieldProps('prenom')}
            value={getFieldProps('prenom').value || ''}
            error={Boolean(touched.prenom && errors.prenom)}
            helperText={touched.prenom && errors.prenom}
          />

          <TextField
            fullWidth
            label={t('fields_LastName')}
            {...getFieldProps('name')}
            value={getFieldProps('name').value || ''}
            error={Boolean(touched.name && errors.name)}
            helperText={touched.name && errors.name}
          />
        </Stack>

        <TextField
          fullWidth
          autoComplete="address"
          label={t('fields_Address')}
          {...getFieldProps('adresse')}
          value={getFieldProps('adresse').value || ''}
          error={Boolean(touched.adresse && errors.adresse)}
          helperText={touched.adresse && errors.adresse}
        />
        <TextField
          fullWidth
          autoComplete="address"
          label={t('fields_ExtraAddress')}
          {...getFieldProps('adresseSuite')}
          value={getFieldProps('adresseSuite').value || ''}
          error={Boolean(touched.adresseSuite && errors.adresseSuite)}
          helperText={touched.adresseSuite && errors.adresseSuite}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label={t('fields_City')}
            {...getFieldProps('ville')}
            value={getFieldProps('ville').value || ''}
            error={Boolean(touched.ville && errors.ville)}
            helperText={touched.ville && errors.ville}
          />

          <TextField
            fullWidth
            label={t('fields_Zip')}
            type="number"
            {...getFieldProps('codePostal')}
            value={getFieldProps('codePostal').value || ''}
            error={Boolean(touched.codePostal && errors.codePostal)}
            helperText={touched.codePostal && errors.codePostal}
          />
        </Stack>

        <CountrySelect formik={formik} />

        <Autocomplete
          fullWidth
          {...getFieldProps('aero')}
          options={aeros}
          getOptionLabel={(option) =>
            option.oaci === undefined ? '' : `${option.oaci} - ${option.nom}`
          }
          value={aeros.find((aero) => aero.oaci === getFieldProps('aero').value) || null}
          renderInput={(params) => <TextField {...params} label={t('fields_Aerodrome')} />}
          onChange={(event, value) => setFieldValue('aero', value !== null ? value.oaci : 0)}
        />

        <TextField
          fullWidth
          autoComplete="telephone"
          type="number"
          label={t('fields_Phone')}
          {...getFieldProps('phone')}
          value={getFieldProps('phone').value || ''}
          error={Boolean(touched.phone && errors.phone)}
          helperText={touched.phone && errors.phone}
        />

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          startIcon={<Icon icon={saveIcon} />}
          onClick={handleSubmit}
        >
          {t('btn_Save')}
        </LoadingButton>
      </Stack>
    </FormikProvider>
  );
}
